import { ClientStatus, IClient } from "intake_shared/src/types";

export const DEFAULT_TEST_RECIPIENT =
    process.env.REACT_APP_AI_INTAKE_TEST_RECIPIENT ?? "";
export const E164_PHONE = /^\+[1-9]\d{7,14}$/;

export interface LeaHandoffResult {
    status: string;
    outbound_enabled: boolean;
}

/** Inquiry statuses Lea AI can take over. The backend re-checks eligibility. */
export function canHandToLea(client: Pick<IClient, "status">): boolean {
    return [ClientStatus.INCOMPLETE_INQUIRY, ClientStatus.INQUIRY].includes(
        client.status
    );
}

export function handoffResultToast(result: LeaHandoffResult): {
    text: string;
    appearance: "success" | "error";
} {
    if (result.outbound_enabled) {
        return {
            text: "Lea AI started the test conversation.",
            appearance: "success",
        };
    }
    if (["waiting_window", "draft_ready"].includes(result.status)) {
        return {
            text: "Lea AI handoff is queued. No SMS has been sent yet.",
            appearance: "success",
        };
    }
    if (result.status === "send_unknown") {
        return {
            text: "The SMS outcome needs review before another attempt.",
            appearance: "error",
        };
    }
    return {
        text: "Lea AI could not start this conversation.",
        appearance: "error",
    };
}

export function handoffErrorMessage(error: unknown): string {
    if (!error || typeof error !== "object") {
        return "Could not hand this inquiry to Lea AI. Please try again.";
    }
    const { code, message } = error as { code?: unknown; message?: unknown };
    const rawMessage = typeof message === "string" ? message.trim() : "";
    let readableMessage = rawMessage;
    if (rawMessage.startsWith("{")) {
        try {
            const parsed = JSON.parse(rawMessage) as {
                error?: { message?: unknown };
            };
            readableMessage =
                typeof parsed.error?.message === "string"
                    ? parsed.error.message
                    : "";
        } catch {
            readableMessage = "";
        }
    }
    if (readableMessage === "Inquiry is ineligible") {
        return "Check SMS consent, inquiry status, and self-pay before handing to Lea AI.";
    }
    if (code === "functions/failed-precondition") {
        return readableMessage || "This inquiry cannot be handed to Lea AI.";
    }
    if (code === "functions/permission-denied") {
        if (readableMessage === "Draft access is disabled") {
            return "Lea AI handoff is currently disabled. Please contact an administrator.";
        }
        if (readableMessage === "Intake permission is required") {
            return "An Intake or super admin role is required to hand off to Lea AI.";
        }
        return "Lea AI handoff was denied. Please contact an administrator.";
    }
    if (code === "functions/deadline-exceeded") {
        return "Handoff is taking longer than expected. Check Comms before trying again.";
    }
    return "Could not hand this inquiry to Lea AI. Please try again.";
}
